import { css } from '@emotion/core';
import { Col, DatePicker, Row } from 'antd';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import Media from 'react-media';

import Form from '@/src/shared/components/Form';
import ListPage from '@/src/shared/components/ListPage';
import useListSearch from '@/src/shared/hooks/useListSearch';
import { convertMomentToDate, datePickerProps, momentize, today } from '@/utils/dateTimeHelpers';
import formatURL from '@/utils/formatURL';
import { colSmProps, rowProps } from '@/utils/rowColHelpers';
import searchContent from '@/utils/searchContent';

const style = {
  notice: css`
    font-size: 1.6rem;
    line-height: 2.8rem;
    color: #3d3d41;
    padding: 0 8px;
  `,
};

const DailyDigest = props => {
  const router = useRouter();
  const { form } = props;
  const { query } = router;

  const currentDate = today();
  const threeDaysAgo = today().subtract(3, 'days');

  const [loading, results] = useListSearch({
    includeOnHomePage: 'Y', // This represents the includeOnDailyDigest flag now.
    date: true,
    dateStart: query.date || convertMomentToDate(threeDaysAgo),
    dateEnd: query.date || convertMomentToDate(currentDate),
  });
  const [isFetching, setIsFetching] = useState(false);
  const [newResults, setNewResults] = useState([]);
  const [fetchedNewResults, setFetchedNewResults] = useState(false);

  useEffect(() => {
    if (query.date) {
      const momentizedDate = momentize(query.date);
      form.setFieldsValue({
        Date: momentizedDate,
      });
    }
  }, []);

  const fetchResults = async date => {
    try {
      setIsFetching(true);
      const response = await searchContent(
        {
          includeOnHomePage: 'Y', // This represents the includeOnDailyDigest flag now.
          date,
          dateStart: date,
          dateEnd: date,
        },
        {
          triggerLoad: false,
        }
      );
      setFetchedNewResults(true);
      setNewResults(response.results);
      setIsFetching(false);
    } catch (error) {
      setNewResults([]);
      setIsFetching(false);
    }
  };

  const handleDateChange = async e => {
    if (!e) {
      const response = await searchContent({
        includeOnHomePage: 'Y', // This represents the includeOnDailyDigest flag now.
        date: true,
        dateStart: convertMomentToDate(threeDaysAgo),
        dateEnd: convertMomentToDate(currentDate),
      });
      router.replace(router.pathname, router.pathname, { shallow: true });
      setFetchedNewResults(true);
      setNewResults(response.results);
    }
    if (e) {
      const date = convertMomentToDate(e);
      const url = formatURL(router.asPath, {
        date,
      });
      router.replace(url, url, { shallow: true });
      fetchResults(date);
    }
  };

  const handleBlur = e => {
    const calendarInput = document.querySelector('.ant-calendar-input');
    if (calendarInput && !calendarInput.readOnly) {
      calendarInput.readOnly = true;
    }
  };

  const listData = fetchedNewResults ? newResults : results.filter(({ SitePageId }) => SitePageId);

  return (
    <ListPage
      title="Daily Digest Postings"
      overrideLast={{
        path: '/daily-digest',
        name: 'Daily Digest Postings',
      }}
      heroContent={
        <p>
          APCIA subject matter experts post business-critical information to the website each day.
          See below for a listing of the most recent postings. You can also search for information
          posted on a particular day by using the search box.{' '}
        </p>
      }
      results={listData}
      loading={loading || isFetching}
      renderResultSubtitle={result => result.PublishDateTime}
      noResultsDisplay={
        !listData.length &&
        !!query.date && (
          <div css={style.notice}>There are no publications available for this date.</div>
        )
      }
      renderBeforeList={() => (
        <Form theme="white">
          <Row {...rowProps}>
            <Col {...colSmProps}>
              <Media query="(max-width: 1024px)">
                {isMD => {
                  const focusProps = isMD ? { onBlur: handleBlur, autoFocus: true } : {};
                  return (
                    <Form.Item>
                      {form.getFieldDecorator('Date')(
                        <DatePicker
                          {...datePickerProps}
                          {...focusProps}
                          onChange={handleDateChange}
                        />
                      )}
                    </Form.Item>
                  );
                }}
              </Media>
            </Col>
          </Row>
        </Form>
      )}
    />
  );
};

export default Form.create({ name: 'DailyDigest' })(DailyDigest);
